import { AnimatedPageHeader } from '@components/index'
import { Table } from '@components/table'
import { useApplicationTable } from '@services/applications/queries/useApplicationTable'
import type { SortOrder } from '@components/table/types'
import { CircleCheck, CircleX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ApplicationList } from 'types/api'
import { BaseResponse } from 'types/utils'

import { cn } from '@/lib/utils'

import { ApplicationsTableFilters } from './ApplicationsTableFilters'

const SORT_DIRECTIONS: SortOrder[] = ['ascend', 'descend']

export function ApplicationsTable() {
  const { t } = useTranslation('application')
  const query = useApplicationTable()

  const columns = [
    {
      title: t('form.labels.users'),
      dataIndex: 'user_detail',
      render: (user_detail: { email: string }) => (
        <span className="font-medium text-ink">{user_detail?.email}</span>
      ),
      sorter: true,
      sortDirections: SORT_DIRECTIONS
    },
    {
      title: t('form.labels.locations'),
      dataIndex: 'locations',
      render: (locations: { name: string }[]) => {
        if (!locations?.length) return '-'
        const names = locations.map((l) => l.name)
        return (
          <div className="flex flex-wrap gap-1">
            {names.slice(0, 2).map((name, i) => (
              <span
                key={i}
                className="rounded-full bg-ink/[0.06] px-2 py-0.5 text-xs font-medium text-ink/70"
              >
                {name}
              </span>
            ))}
            {names.length > 2 && (
              <span
                title={names.slice(2).join(', ')}
                className="rounded-full bg-ink/[0.06] px-2 py-0.5 text-xs font-medium text-ink/50"
              >
                +{names.length - 2}
              </span>
            )}
          </div>
        )
      }
    },
    {
      title: t('form.labels.rent_range'),
      dataIndex: 'rent_min',
      render: (_: unknown, record: ApplicationList) => {
        if (!record.rent_min && !record.rent_max) return '-'
        return (
          <span className="whitespace-nowrap font-semibold text-ink">
            {record.rent_min ?? '–'}
            {record.rent_min ? ' €' : ''}{' '}
            <span className="font-normal text-muted-foreground">–</span>{' '}
            {record.rent_max ?? '–'}
            {record.rent_max ? ' €' : ''}
          </span>
        )
      },
      sorter: true,
      sortDirections: SORT_DIRECTIONS
    },
    {
      title: t('form.labels.arrival_date'),
      dataIndex: 'arrival_date',
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      render: (date: string) =>
        date ? new Date(date).toLocaleDateString('sk-SK') : '-'
    },
    {
      title: t('form.labels.status'),
      dataIndex: 'status',
      sorter: true,
      sortDirections: SORT_DIRECTIONS,
      render: (status: boolean) => (
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold',
            status ? 'bg-primary/15 text-primary' : 'bg-ink/[0.07] text-ink/60'
          )}
        >
          {status ? (
            <CircleCheck className="h-3.5 w-3.5" />
          ) : (
            <CircleX className="h-3.5 w-3.5" />
          )}
          {status ? t('form.common.active') : t('form.common.inactive')}
        </span>
      )
    }
  ]

  return (
    <div className="space-y-6">
      <AnimatedPageHeader title={t('form.labels.applications')} />
      <ApplicationsTableFilters />
      <Table<BaseResponse<ApplicationList>>
        query={query}
        columns={columns}
        id="applications"
        hasSearch={false}
      />
    </div>
  )
}
